/*
 * Note:    This section sets up the API Call Factories as well as base types
 *          that will allow custom API Call types to be built.
 *          Generally this default type will cover most scenarios as it
 *          implements a fetch call and handles the authentication and output.
 *          An example where the Custom API Call stuff comes in handy, and the
 *          original reason for their creation was to create a seamless interface
 *          with ArcGIS Server's Geoprocessing Service, where a request kicks off
 *          a process, then the process Id is used to poll the status of the process
 *          and when completed the result is then another request.
 *          A single custom API Call Factory can be created to follow this process
 *          while mapped to a single awaitable API Call.
 */

import { ThunkDispatch } from '@reduxjs/toolkit';
import { Action } from '@reduxjs/toolkit';

import { sleep } from 'utils';
import { RootState } from 'store';
import { AuthActions } from 'state/auth';

import * as AuthManager from './auth';

/**
 * A list of error codes to be used by the application, primarily for the purpose of logging and error statistics.
 */
export enum ErrorTypes {
    // 0-99 kept for misc application errors (although doesn't have to be strict)
    NOT_ENOUGH_DETAIL = 10,
    INVALID_CREDENTIALS = 20,
    FAILED_TO_SEND_REQUEST = 30,
    // 200 'everything is okay' errors
    OK = 200,
    // 400 errors are web related client errors
    BAD_REQUEST = 400,
    UNAUTHORISED = 401,
    FORBIDDEN = 403,
    NOT_FOUND = 404,
    IM_A_TEAPOT = 418, // Strictly for completeness reasons: https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/418
    // 500 errors are web related server side errors
    SERVER_ERROR = 500,
    NOT_IMPLEMENTED = 501,
    BAD_GATEWAY = 502,
    SERVICE_UNAVAILABLE = 503,
    ESRI_ERROR = 990,
    GEOPROCESSING_FAILED = 991,
    MOCK_ERROR_CODE = 999,
}

/**
 * An interface used to store error details when the
 * server responds with an error
 */
export interface ErrorResp {
    error: string;
    code: ErrorTypes;
    data?: any;
}

/**
 * @param obj An object to check
 * @returns True if the object is an Error response type, and enforces the type.
 */
export function isErrorResp(obj: any): obj is ErrorResp {
    return typeof obj === 'object' && obj?.error != null && obj?.code != null;
}

/**
 * @param obj An object that may be an error response but with an unknown type
 * @returns The object cast to an errorResp if one, otherwise the object case
 * to the given generic
 */
export function castToIfNotErrorResp<T>(obj: any): T | ErrorResp {
    if (isErrorResp(obj)) {
        return obj;
    }
    return obj as T;
}

/**
 * Will raise an error if the object is seen to be an ErrorResp type.
 * @param obj The object to check
 * @returns The object cast to a generic if an error isn't raised
 */
export function raiseIfError<T>(obj: any): T {
    if (isErrorResp(obj)) {
        throw obj;
    } else {
        return obj as T;
    }
}

/**
 * A Standard initial payload interface for a basic API call.
 * The API Call callback will have to return this interface
 * or for a more custom API call, an interface that extends this.
 */
export interface APIInterfacePayload {
    /**
     * The Full destination url
     */
    url: string;
    /**
     * Fetch settings
     */
    requestData?: RequestInit;
    /**
     * If forcing a content type, set to the desired content type
     */
    contentType?: string | null;
    /**
     * The type of response data. Dictates how the response is
     * processed. For example if the response is in json the
     * Fetch result will call the response.json() method.
     */
    dataType?: string;
    /**
     * An optional abort signal to send to the fetch request.
     */
    abortSignal?: AbortSignal | null;
    /**
     * A flag for if authentication should be skipped.
     * True means to not add an auth token. Falsy values otherwise.
     */
    skipAuth?: boolean;
}

/**
 * Specifies the format an API Call implementation that can be attached to a
 * custom API Call Factories.
 */
export type APIInterface<T extends APIInterfacePayload = APIInterfacePayload> =
    (
        payload: T,
        state?: RootState,
        dispatch?: ThunkDispatch<RootState, unknown, Action<string>>,
    ) => Promise<any | ErrorResp>;

/**
 * Is the signature of the API Call Callback Function
 * API Call Payloads should be specified in the first generic
 * while the 2nd Generic uses the default APICall Interface Payload
 * but can be replaced with any custom one.
 */
export type APICallbackType<
    Payload = void,
    CBPayload extends APIInterfacePayload = APIInterfacePayload,
> = (
    payload: Payload,
    state?: RootState,
    dispatch?: ThunkDispatch<RootState, unknown, Action<string>>,
) => CBPayload;

/**
 * The Signature of the API Call Mapping Function which is
 * intended to process the results of the API Call into a
 * format more useful to the application.
 */
export type APIMapFunctionType<Obj, Payload = void, RawAPIResponse = any> = (
    response: RawAPIResponse,
    payload: Payload,
    state?: RootState,
    dispatch?: ThunkDispatch<RootState, unknown, Action<string>>,
) => Obj | Promise<Obj>;

/**
 *
 * @param apiCall The implementation of the API Call to use
 * @param cb The callback that should be mapped through the resulting API Call Factory
 * @param map The map function that should
 * @returns An API Call Factory function that generates API Calls for the given apiCall implementation
 */
export const abstractAPICallFactory = <
    Obj,
    Payload = void,
    RawAPIResponse = any,
    CBPayload extends APIInterfacePayload = APIInterfacePayload,
>(
    apiCall: APIInterface<CBPayload>,
    cb: APICallbackType<Payload, CBPayload>,
    map: APIMapFunctionType<Obj, Payload, RawAPIResponse> = (resp) =>
        resp as any as Obj,
) => {
    return async (
        payload: Payload,
        state?: RootState,
        dispatch?: ThunkDispatch<RootState, unknown, Action<string>>,
    ): Promise<Obj> => {
        const data: CBPayload = {
            requestData: {},
            contentType: 'application/json',
            ...cb(payload, state, dispatch),
        };

        return map(
            raiseIfError<RawAPIResponse>(await apiCall(data, state, dispatch)),
            payload,
            state,
            dispatch,
        );
    };
};

export const mapCodeToMessage: Record<number, string> = {
    10: 'You cannot send this request as not enough detail has been provided',
    20: 'You have no credentials or your credentials are invalid',
    30: "An error occured while sending the request. This may be due to a failed intenet connection or the server didn't respond.",
    400: 'Bad request sent to server',
    401: 'You are not authorised to view this resource',
    403: 'You are not authorised to view this resource',
    404: 'URL on server cannot be found',
    418: 'Teapot Error',
    990: 'ESRI Error',
    991: 'Geoprocessing Task Failed',
    999: 'This is just a test error for Mock requests',
};

const parser = new DOMParser();

/**
 * A generic API Call that adds Authentication to the request, attempts to handle
 * token management if necessary and will process the reponse into a type as requested.
 *
 * Warning: This function is not designed to be called directly, and should be
 * managed using {@link makeAPICall} as a factory and matched with
 * `makeThunkFromAPICall` in the state section.
 *
 */
export const callAPI: APIInterface = async (payload, state, dispatch) => {
    const pl = {
        requestData: {} as RequestInit,
        contentType: 'application/json',
        dataType: 'json',
        ...payload,
    };
    const requestHeaders: HeadersInit = new Headers();

    // Added auto to support file uploads
    if (pl.contentType && pl.contentType != 'auto')
        requestHeaders.set('Content-Type', pl.contentType);

    const tokens = state?.auth?.tokens?.object;
    if (tokens?.accessToken && tokens.accessToken.length > 0) {
        requestHeaders.set(
            'Authorization',
            `${tokens.tokenType} ${tokens.accessToken}`,
        );
    }
    pl.requestData.headers = requestHeaders;

    pl.requestData.signal = pl.abortSignal;
    try {
        // TODO need a better fix for this
        if (pl.requestData.method == undefined && pl.url.indexOf('?') != -1) {
            pl.url = `${pl.url}&rand=${Date.now()}`;
        } else if (pl.requestData.method == undefined) {
            pl.url = `${pl.url}?rand=${Date.now()}`;
        }
        let resp = await fetch(pl.url, pl.requestData);
        if (resp.status === 401) {
            // There is either an issue with the token or no token when there should be
            if (dispatch && tokens?.refreshToken) {
                // retry loading the token
                // reusing the existing refreshToken action appears not
                // to be typed properly or not allowed

                dispatch(
                    AuthActions.setTokensStatus({
                        status: 'loading',
                    }),
                );
                const tokenResp = await AuthManager.getToken({ tokens } as any);
                if (isErrorResp(tokenResp)) {
                    // if loading the token failed, then we return an error
                    dispatch(
                        AuthActions.setTokensStatus({
                            status: 'error',
                            error: tokenResp,
                        }),
                    );
                    return {
                        error:
                            resp.statusText ||
                            mapCodeToMessage[resp.status] ||
                            '',
                        code: resp.status,
                        data: { refreshError: tokenResp },
                    };
                }

                await dispatch(
                    AuthActions.setTokensStatus({
                        status: 'finished',
                    }),
                );
                await dispatch(
                    AuthActions.setTokens({
                        refreshToken: state?.auth?.tokens.object?.refreshToken,
                        ...tokenResp,
                    }),
                );

                const newHeaders: Record<string, string> = {};

                if (
                    tokenResp?.accessToken &&
                    tokenResp.accessToken.length > 0
                ) {
                    newHeaders.Authorization = `${tokenResp.tokenType} ${tokenResp.accessToken}`;
                }

                // now lets retry the request with the new data
                resp = await fetch(pl.url, {
                    ...pl.requestData,
                    headers: newHeaders,
                });
            }
        }
        if (!resp.ok) {
            if (resp.status === 401) {
                console.error(
                    "Redirecting to login page as the API returned a 401 Error and an attempt to Refresh the token didn't work.",
                );
                // at this point the refresh token will have already been tried
                // or not available, so we should just redirect to the login page
                AuthManager.redirectToAuth();
            }
            return {
                error: resp.statusText || mapCodeToMessage[resp.status] || '',
                code: resp.status,
                data: await resp.json(),
            };
        }
        if (pl.dataType === 'blob') {
            return await resp.blob();
        }

        if (pl.dataType === 'xml')
            return parser.parseFromString(await resp.text(), 'application/xml');

        if (pl.dataType === 'text') return await resp.text();

        return await resp.json();
    } catch (err) {
        return {
            error: mapCodeToMessage[ErrorTypes.FAILED_TO_SEND_REQUEST],
            code: ErrorTypes.FAILED_TO_SEND_REQUEST,
            data: err,
        } as ErrorResp;
    }
};

/**
 * An API Call Factory which accepts up to 3 Generics and 2 functions to define the API Call function to be built.
 * The 3 Generics in order are as follows:
 * - Obj: The type to be returned
 * - Payload: (void) The type of the payload to be provided to the function call
 * - RawAPIResponse: (any) The type of the expected Raw API response
 *
 * The two parameters are the pre and post API call functions.
 * The first is intended to map the Payload + Redux State to help init the request.
 * The second is intended to map the RawAPIResponse generic to the Obj generic.
 *
 * Using this function for building API Calls is preferable as it handles Authentication and error handling
 * in a manner that integrates well with building a Thunk Action (see: /src/actions/index.ts:makeThunkFromAPICall)
 *
 * @param cb A function that accepts the defined Payload + Redux state and returns request information.
 * @param map A function that maps the requests response to the specified output object Obj
 *
 * @returns An Async API Call ready to be used called within a Thunk Action
 */
export const makeAPICall = <Obj, Payload = void, RawAPIResponse = any>(
    cb: APICallbackType<Payload>,
    map: APIMapFunctionType<Obj, Payload, RawAPIResponse>,
) => abstractAPICallFactory<Obj, Payload, RawAPIResponse>(callAPI, cb, map);

/**
 * The same as {@link makeAPICall} including containing the same structure, however with extra parameters.
 * Instead of performing some API Call to fetch data, the extra parameters provide the response information
 * allowing for API Call's to be Mocked and used up until the API has been implemented. Can also be used
 * for tests.
 *
 * @param cb A function that accepts the defined Payload + Redux state and returns request information.
 * @param map A function that maps the requests response to the specified output object Obj
 * @param mockData The data to be responded with
 * @param maxWaitTime Default: 1000; The maximum time in milliseconds before this API Call returns the
 * mock result. A random number between 0 and this will be chosen as the waiting time.
 * @param failureRate Default: 0; A value between 0 and 1 which indicates a test failure rate.
 * Useful for testing error cases.
 * @returns An Async API Call ready to be used called within a Thunk Action that matches the signature
 * of {@link makeAPICall}.
 */
export function makeMockAPICall<Obj, Payload = void, RawAPIResponse = any>(
    cb: APICallbackType<Payload, any>,
    map: APIMapFunctionType<Obj, Payload, RawAPIResponse>,
    mockData?: RawAPIResponse,
    maxWaitTime = 1000,
    failureRate = 0,
) {
    return async (
        payload: Payload,
        state?: RootState,
        dispatch?: ThunkDispatch<RootState, unknown, Action<string>>,
    ): Promise<Obj> => {
        // simulate waiting for server to respond
        cb(payload, state, dispatch);
        if (maxWaitTime > 0) await sleep(Math.random() * maxWaitTime);
        if (failureRate && Math.random() < failureRate)
            throw {
                error: `Mock API Call Test Error (error rate ${
                    failureRate * 100
                }%)`,
                code: ErrorTypes.MOCK_ERROR_CODE,
            };
        return map(mockData as RawAPIResponse, payload, state, dispatch);
    };
}
